"use client";

import { c as _c } from "react/compiler-runtime";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { useEmoji } from "@/contexts/EmojiContext";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import { numberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { INotificationDropBoosted, INotificationDropReacted, INotificationDropVoted } from "@/types/feed.types";
import ReactionEmojiPreview from "./ReactionEmojiPreview";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
export const getNotificationVoteColor = (vote: number) => {
  if (vote > 0) return "tw-text-green";
  if (vote < 0) return "tw-text-red";
  return "tw-text-iron-500";
};
type NotificationUnion = INotificationDropVoted | INotificationDropReacted | INotificationDropBoosted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(63);
  const {
    notification,
    activeDrop,
    onReply,
    onDropContentClick
  } = t0;
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const isVoted = notification.cause === ApiNotificationCause.DropVoted;
  const isReacted = notification.cause === ApiNotificationCause.DropReacted;
  const isBoosted = notification.cause === ApiNotificationCause.DropBoosted;
  let actionElement;
  if (isVoted) {
    const voteValue = notification.additional_context.vote;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">rated</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== voteValue) {
      t2 = getNotificationVoteColor(voteValue);
      $[1] = voteValue;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = `${t2} tw-text-sm tw-font-medium`;
    const t4 = voteValue > 0 && "+";
    let t5;
    if ($[3] !== voteValue) {
      t5 = numberWithCommas(voteValue);
      $[3] = voteValue;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
      t6 = <span className={t3}>{t4}{t5}</span>;
      $[5] = t3;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== notification.created_at) {
      t7 = <NotificationTimestamp createdAt={notification.created_at} />;
      $[9] = notification.created_at;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t6 || $[12] !== t7) {
      t8 = <>{t1}{t6}{t7}</>;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    actionElement = t8;
  } else {
    if (isBoosted) {
      let t1;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">🔥 boosted 🔥</span>;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      let t2;
      if ($[15] !== notification.created_at) {
        t2 = <>{t1}<NotificationTimestamp createdAt={notification.created_at} /></>;
        $[15] = notification.created_at;
        $[16] = t2;
      } else {
        t2 = $[16];
      }
      actionElement = t2;
    } else {
      if (isReacted) {
        let rawId;
        let t1;
        if ($[17] !== findCustomEmoji || $[18] !== findNativeEmoji || $[19] !== notification.additional_context.reaction) {
          rawId = notification.additional_context.reaction.replaceAll(":", "");
          t1 = !findCustomEmoji(rawId) && !findNativeEmoji(rawId);
          $[17] = findCustomEmoji;
          $[18] = findNativeEmoji;
          $[19] = notification.additional_context.reaction;
          $[20] = rawId;
          $[21] = t1;
        } else {
          rawId = $[20];
          t1 = $[21];
        }
        if (t1) {
          return null;
        }
        let t2;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">reacted</span>;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        let t3;
        if ($[23] !== rawId) {
          t3 = <ReactionEmojiPreview rawId={rawId} />;
          $[23] = rawId;
          $[24] = t3;
        } else {
          t3 = $[24];
        }
        let t4;
        if ($[25] !== notification.created_at) {
          t4 = <NotificationTimestamp createdAt={notification.created_at} />;
          $[25] = notification.created_at;
          $[26] = t4;
        } else {
          t4 = $[26];
        }
        let t5;
        if ($[27] !== t3 || $[28] !== t4) {
          t5 = <>{t2}{t3}{t4}</>;
          $[27] = t3;
          $[28] = t4;
          $[29] = t5;
        } else {
          t5 = $[29];
        }
        actionElement = t5;
      } else {
        return null;
      }
    }
  }
  const drop = notification.related_drops[0];
  if (!drop) {
    return null;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[30] !== actionElement || $[31] !== activeDrop || $[32] !== createQuoteClickHandler || $[33] !== createReplyClickHandler || $[34] !== drop || $[35] !== notification.related_identity || $[36] !== onReply) {
    const isDirectMessage = getIsDirectMessage(drop.wave);
    t6 = "tw-flex tw-w-full tw-flex-col tw-space-y-2";
    let t8;
    if ($[45] !== notification.related_identity) {
      t8 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
      $[45] = notification.related_identity;
      $[46] = t8;
    } else {
      t8 = $[46];
    }
    if ($[47] !== actionElement || $[48] !== notification.related_identity || $[49] !== t8) {
      t7 = <NotificationHeader author={notification.related_identity} actions={t8}>{actionElement}</NotificationHeader>;
      $[47] = actionElement;
      $[48] = notification.related_identity;
      $[49] = t8;
      $[50] = t7;
    } else {
      t7 = $[50];
    }
    T0 = NotificationDrop;
    t1 = drop;
    t2 = activeDrop;
    t3 = onReply;
    t4 = createReplyClickHandler(drop.wave.id, isDirectMessage);
    t5 = createQuoteClickHandler(isDirectMessage);
    $[30] = actionElement;
    $[31] = activeDrop;
    $[32] = createQuoteClickHandler;
    $[33] = createReplyClickHandler;
    $[34] = drop;
    $[35] = notification.related_identity;
    $[36] = onReply;
    $[37] = T0;
    $[38] = t1;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
  } else {
    T0 = $[37];
    t1 = $[38];
    t2 = $[39];
    t3 = $[40];
    t4 = $[41];
    t5 = $[42];
    t6 = $[43];
    t7 = $[44];
  }
  let t8;
  if ($[51] !== T0 || $[52] !== onDropContentClick || $[53] !== t1 || $[54] !== t2 || $[55] !== t3 || $[56] !== t4 || $[57] !== t5) {
    t8 = <T0 drop={t1} activeDrop={t2} onReply={t3} onReplyClick={t4} onQuoteClick={t5} onDropContentClick={onDropContentClick} />;
    $[51] = T0;
    $[52] = onDropContentClick;
    $[53] = t1;
    $[54] = t2;
    $[55] = t3;
    $[56] = t4;
    $[57] = t5;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== t6 || $[60] !== t7 || $[61] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[59] = t6;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  return t9;
}